/**
 * Open the OS share sheet with a URL.
 *
 * Order of preference:
 *   1. `navigator.share` — the Web Share API. Works in the currently
 *      deployed Capacitor `.aab` on Android because the WebView is
 *      Chrome-based and supports it. MUST be called synchronously inside
 *      a user-gesture handler (no long `await` before this call) — otherwise
 *      Chromium rejects with `NotAllowedError`.
 *   2. `@capacitor/share` native plugin — only registered in native
 *      Android/iOS builds that ship with the plugin (future `.aab`).
 *      Reliable regardless of WebView user-gesture rules.
 *   3. Clipboard fallback — desktop browsers, or WebViews where both
 *      of the above fail.
 *
 * Returns { method: 'share' | 'copy' | 'failed', cancelled?: boolean }.
 */
import { Capacitor } from '@capacitor/core';
import { Share as CapacitorShare } from '@capacitor/share';

export async function shareOrCopy({ title, text, url } = {}) {
  const hasUrl = typeof url === 'string' && url.length > 0;
  const hasText = typeof text === 'string' && text.length > 0;
  if (!hasUrl && !hasText) return { method: 'failed' };

  // If the caller already inlined `url` inside `text` (e.g. host invite
  // messages that append the invite link at the bottom), do NOT also pass
  // `url` to the share target. Several apps — Facebook Messenger, WhatsApp
  // on some devices — concatenate `text` and `url`, which produces a
  // duplicated invite link in the outgoing message.
  const urlInText = hasUrl && hasText && text.includes(url);
  const sharePayload = {};
  if (title) sharePayload.title = title;
  if (hasText) sharePayload.text = text;
  if (hasUrl && !urlInText) sharePayload.url = url;

  // Clipboard-only content = whatever the user should see pasted. When the
  // caller supplied a message, the message (which already contains the URL
  // when applicable) is what belongs in the clipboard — otherwise the URL.
  const clipContent = hasText ? text : url;

  // 1. Web Share API — try first because it preserves the user-gesture
  // window on Android WebView / Chrome without any bridge round-trip.
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share(sharePayload);
      return { method: 'share' };
    } catch (err) {
      if (err && err.name === 'AbortError') {
        return { method: 'share', cancelled: true };
      }
      // Fall through — will try Capacitor plugin (if native) or clipboard.
    }
  }

  // 2. Capacitor native Share plugin — only relevant once the .aab is
  // rebuilt with `@capacitor/share`. Guarded by `isNativePlatform()`.
  if (Capacitor?.isNativePlatform && Capacitor.isNativePlatform()) {
    try {
      await CapacitorShare.share({ ...sharePayload, dialogTitle: title });
      return { method: 'share' };
    } catch (err) {
      if (err && /cancel/i.test(err.message || '')) {
        return { method: 'share', cancelled: true };
      }
      // Plugin not installed or native error → clipboard fallback.
    }
  }

  // 3. Clipboard fallback.
  try {
    await navigator.clipboard.writeText(clipContent);
    return { method: 'copy' };
  } catch (_clipErr) {
    try {
      const ta = document.createElement('textarea');
      ta.value = clipContent;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return { method: 'copy' };
    } catch { /* ignore */ }
    return { method: 'failed' };
  }
}
